import React, { useEffect, useMemo, useState } from 'react';
import { Neighborhood } from './ListingsMap';

interface Props {
  ids: string[];
  neighbourhoods: Pick<Neighborhood, 'id' | 'name'>[];
  onRemove: (id: string) => void;
  onClearAll?: () => void;
  onOpenMap?: () => void;
  limit: number;
  scrollable?: boolean;
}

export default function CommuteChipsList({
  ids,
  neighbourhoods,
  onRemove,
  onClearAll,
  onOpenMap,
  limit,
  scrollable = false,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  const nameMap = useMemo(
    () => new Map(neighbourhoods.map(n => [n.id, n.name])),
    [neighbourhoods]
  );

  useEffect(() => {
    if (ids.length <= limit) setExpanded(false);
  }, [ids.length, limit]);

  if (ids.length === 0) return null;

  const hasMore = ids.length > limit;
  const visibleIds = hasMore && !expanded ? ids.slice(0, limit) : ids;
  const extraCount = ids.length - limit;

  return (
    <div className="commute-chips">
      <div className={`commute-chips__items${scrollable && expanded ? ' commute-chips__items--scrollable' : ''}`}>
        {visibleIds.map(id => {
          const name = nameMap.get(id) ?? id;
          return (
            <button
              key={id}
              className="commute-chips__item"
              aria-label={`Remove ${name}`}
              onClick={() => onRemove(id)}
            >
              <span className="commute-chips__label">{name}</span>
            </button>
          );
        })}
        {hasMore && (
          <button className="commute-chips__expand" onClick={() => setExpanded(e => !e)}>
            {expanded ? 'Show less' : `+${extraCount} more`}
          </button>
        )}
      </div>
      {(onClearAll || onOpenMap) && (
        <div className="commute-chips__controls">
          {onClearAll && (
            <button className="commute-chips__clear" aria-label="Clear all areas" onClick={onClearAll}>
              ×
            </button>
          )}
          {onOpenMap && (
            <button className="hs-map-pin-btn" aria-label="Open map" onClick={onOpenMap}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
